import type { TradeLogicDraft } from '../model';

export const TRADE_LOGIC_AUTHORING_DRAFT_STORAGE_VERSION = 2;

const storageKeyPrefix = 'p314:tradeLogicAuthoringDraft:v2:strategy';

interface TradeLogicAuthoringDraftStorage {
    getItem: (key: string) => string | null;
    removeItem: (key: string) => void;
    setItem: (key: string, value: string) => void;
}

export interface TradeLogicAuthoringDraftPersistenceKey {
    scopeKey?: string | null;
    strategyId: number | null;
}

export interface TradeLogicAuthoringDraftWorkspaceSnapshot {
    version: typeof TRADE_LOGIC_AUTHORING_DRAFT_STORAGE_VERSION;
    savedAt: string;
    strategyId: number | null;
    registryManifestFingerprint: string | null;
    activePayloadDraftKind?: 'entry' | 'exit';
    entryDraft: TradeLogicDraft;
    exitDraft: TradeLogicDraft;
    lastLocalValidationAt: string | null;
}

export interface TradeLogicAuthoringDraftPersistenceAdapter {
    clearWorkspaceDraft: (key: TradeLogicAuthoringDraftPersistenceKey) => void;
    loadWorkspaceDraft: (key: TradeLogicAuthoringDraftPersistenceKey) => TradeLogicAuthoringDraftWorkspaceSnapshot | null;
    saveWorkspaceDraft: (key: TradeLogicAuthoringDraftPersistenceKey, snapshot: TradeLogicAuthoringDraftWorkspaceSnapshot) => void;
}

export const createLocalStorageTradeLogicAuthoringDraftPersistenceAdapter = (
    storage: TradeLogicAuthoringDraftStorage | null = getDefaultStorage()
): TradeLogicAuthoringDraftPersistenceAdapter => ({
    clearWorkspaceDraft: (key) => {
        if (!storage) return;

        try {
            storage.removeItem(buildTradeLogicAuthoringDraftStorageKey(key));
        } catch {
            // Local draft persistence is opportunistic; authoring state remains in memory.
        }
    },
    loadWorkspaceDraft: (key) => {
        if (!storage) return null;

        try {
            return parseTradeLogicAuthoringDraftWorkspaceSnapshot(storage.getItem(buildTradeLogicAuthoringDraftStorageKey(key)));
        } catch {
            return null;
        }
    },
    saveWorkspaceDraft: (key, snapshot) => {
        if (!storage) return;

        try {
            storage.setItem(buildTradeLogicAuthoringDraftStorageKey(key), JSON.stringify(snapshot));
        } catch {
            // Local draft persistence is opportunistic; authoring state remains in memory.
        }
    }
});

export const buildTradeLogicAuthoringDraftStorageKey = ({ scopeKey, strategyId }: TradeLogicAuthoringDraftPersistenceKey) =>
    scopeKey ? `${storageKeyPrefix}:${scopeKey}` : `${storageKeyPrefix}:${strategyId ?? 'new'}`;

export const parseTradeLogicAuthoringDraftWorkspaceSnapshot = (
    rawSnapshot: string | null
): TradeLogicAuthoringDraftWorkspaceSnapshot | null => {
    if (!rawSnapshot) return null;

    try {
        const parsed: unknown = JSON.parse(rawSnapshot);
        return isWorkspaceSnapshot(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

const getDefaultStorage = (): TradeLogicAuthoringDraftStorage | null => (typeof window === 'undefined' ? null : window.localStorage);

const isWorkspaceSnapshot = (value: unknown): value is TradeLogicAuthoringDraftWorkspaceSnapshot => {
    if (!isRecord(value)) return false;

    return (
        value.version === TRADE_LOGIC_AUTHORING_DRAFT_STORAGE_VERSION &&
        typeof value.savedAt === 'string' &&
        (typeof value.strategyId === 'number' || value.strategyId === null) &&
        (typeof value.registryManifestFingerprint === 'string' || value.registryManifestFingerprint === null) &&
        (value.activePayloadDraftKind === undefined ||
            value.activePayloadDraftKind === 'entry' ||
            value.activePayloadDraftKind === 'exit') &&
        isRecord(value.entryDraft) &&
        isRecord(value.exitDraft) &&
        (typeof value.lastLocalValidationAt === 'string' || value.lastLocalValidationAt === null)
    );
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
